import { Component, computed, input, ChangeDetectionStrategy } from '@angular/core';

@Component({
  selector: 'cd-logo',
  standalone: true,
  template: `
    @if (isImage()) {
      @if (isPractina()) {
        <img src="/Practina_Logo.svg" alt="Practina" class="cd-dynamic-logo cd-practina-logo" />
      } @else if (isLendee()) {
        <img src="/lendee-logo.svg" alt="Lendee" class="cd-dynamic-logo cd-lendee-logo" />
      } @else {
        <img src="https://www.denefits.com/wp-content/uploads/2024/09/Logo.svg" alt="Denefits" class="cd-dynamic-logo cd-denefits-logo" />
      }
    } @else {
      <div class="cd-loadx__brand" [class.cd-loadx__brand--animated]="animate()"><b>{{ firstChar() }}</b>{{ restChar() }}<span>.</span></div>
    }
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [`
    :host {
      display: inline-flex;
      align-items: center;
      line-height: 1;
    }
    .cd-dynamic-logo {
      display: block;
      width: auto;
      max-width: 100%;
      height: auto;
    }
    /* Both wordmarks scale off the caller's font-size, so a header that sets
       20px and a modal that sets 30px stay in proportion across products. */
    .cd-lendee-logo,
    .cd-practina-logo {
      height: 1.5em;
    }
    /* --cd-logo-body / --cd-logo-accent come from the product's demoTheme (applyDemoTheme). The
       chained fallbacks keep every pre-existing call site rendering exactly as before, including the
       global loader overlay, which sits outside .cd-root and so resolves to the literals. */
    .cd-loadx__brand {
      font-size: 1.35em;
      font-weight: 800;
      letter-spacing: -0.02em;
      color: var(--cd-logo-body, var(--cd-brand, #0e5a6e));
      line-height: 1;
      display: inline-flex;
      align-items: baseline;
    }
    .cd-loadx__brand b {
      font-weight: inherit;
      color: var(--cd-logo-accent, var(--cd-logo-yellow, var(--cd-amber, #f5a623)));
    }
    .cd-loadx__brand span {
      display: inline-block;
      position: relative;
      color: var(--cd-logo-accent, var(--cd-logo-yellow, var(--cd-amber, #f5a623)));
      transform-origin: 50% 100%;
    }
    .cd-loadx__brand--animated span {
      animation: cdlxDot 1.5s cubic-bezier(0.34, 0, 0.42, 1) infinite;
    }
    @keyframes cdlxDot {
      0%, 100% { transform: translateY(0) scale(1, 1); }
      12% { transform: translateY(0) scale(1.25, 0.7); }
      40% { transform: translateY(-0.55em) scale(0.95, 1.05); }
      68% { transform: translateY(0) scale(1, 1); }
      76% { transform: translateY(0) scale(1.3, 0.65); }
      88% { transform: translateY(0) scale(0.95, 1.05); }
    }
    /* Keep a pulse so the loader still reads as "working", without the bounce. */
    @media (prefers-reduced-motion: reduce) {
      .cd-loadx__brand--animated span {
        animation: cdlxCalm 2s ease-in-out infinite;
      }
    }
    @keyframes cdlxCalm {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.45; }
    }
  `]
})
export class CredeeDemoLogoComponent {
  productId = input<string>('credee');
  productName = input<string>('');
  animate = input<boolean>(false);
  /**
   * `auto` uses the product's official logo image when it has one. `wordmark` forces the CSS text
   * lockup — the bouncing-dot treatment — even for a product with an image, which is what the loader
   * wants: the official SVGs are static, and a motionless loader reads as a hung page.
   */
  variant = input<'auto' | 'wordmark'>('auto');
  /**
   * `title` capitalises the wordmark's initial ("Denefits."); `lower` keeps Credee's all-lowercase.
   * `plain` means the product prefers its name set as-is (its real lockup is an image) — the wordmark
   * still renders, just without forcing a case.
   */
  wordmarkCase = input<'lower' | 'title' | 'plain'>('lower');

  /** Which product to brand as. The call screen clamps `productId` to credee|practina,
   *  so a Denefits call arrives as productId="credee" + productName="Denefits" — the
   *  NAME is the truth whenever it is given; the id is the legacy fallback. */
  private readonly product = computed(() =>
    (this.productName() || this.productId() || '').trim().toLowerCase());

  readonly isPractina = computed(() => this.product() === 'practina');
  readonly isDenefits = computed(() => this.product() === 'denefits');
  readonly isLendee = computed(() => this.product() === 'lendee');
  /** True when this product has an official logo image AND the caller wants it. */
  readonly isImage = computed(
    () => this.variant() === 'auto' && (this.isPractina() || this.isDenefits() || this.isLendee()));

  readonly displayName = computed(() => this.productName() || this.productId() || 'Credee');
  readonly firstChar = computed(() => {
    const first = this.displayName().charAt(0);
    switch (this.wordmarkCase()) {
      case 'title': return first.toUpperCase();
      case 'plain': return first;
      default: return first.toLowerCase();
    }
  });
  readonly restChar = computed(() =>
    this.wordmarkCase() === 'plain'
      ? this.displayName().slice(1)
      : this.displayName().slice(1).toLowerCase());
}
